import React, {
  ReactNode,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { mergeClass } from "../../utils/common";
import { Swiper, SwiperSlide } from "swiper/react";
import Utils from "../../utils/utils";
import { useResponsive } from "../../hooks/useResponsive";
interface TabsProps {
  tabs: ReactNode[];
  classTabs?: string;
  classInner?: string;
}

const Tabs: React.FC<TabsProps> = ({ tabs, classTabs, classInner }) => {
  const { isDesktop } = useResponsive();
  const [activeTab, setActiveTab] = useState(0);
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const lineRef = useRef<HTMLDivElement | null>(null);
  const tabButtonsRef = useRef<HTMLButtonElement[]>([]);

  // Get the header height
  const updateHeaderHeight = useCallback(() => {
    const header = document.querySelector("header");
    if (header) {
      setHeaderHeight(header.clientHeight);
    } else {
      setHeaderHeight(0);
    }
  }, []);

  const handleTabClick = (section: number) => {
    const sectionElement = document.getElementsByClassName("section")[
      section
    ] as HTMLElement;

    if (sectionElement) {
      const rect = sectionElement.getBoundingClientRect();
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      window.scrollTo({
        top: scrollTop + rect.top - headerHeight - 48, // Offset for header
        behavior: "smooth",
      });
    }
  };

  const handleScroll = useCallback(() => {
    if (!tabsRef.current) return;
    const sections = document.getElementsByClassName("section");
    let newActiveTab = activeTab;

    Array.from(sections).forEach((section, index) => {
      const sectionElement = section as HTMLElement;
      const rect = sectionElement.getBoundingClientRect();
      const offset = 100;
      if (
        rect.top <= headerHeight + offset &&
        rect.bottom >= headerHeight + offset
      ) {
        newActiveTab = index;
      }
    });

    setActiveTab(newActiveTab);
  }, [headerHeight, activeTab]);

  useEffect(() => {
    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);
    return () => {
      window.removeEventListener("resize", updateHeaderHeight);
    };
  }, [updateHeaderHeight]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    if (!tabsRef.current || !lineRef.current) return;

    const tabButtons = Array.from(
      tabsRef.current.getElementsByTagName("button"),
    );
    tabButtonsRef.current = tabButtons;

    const activeTabButton = tabButtons[activeTab];

    if (activeTabButton) {
      tabButtons.forEach((tab) => {
        tab.classList.add("text-[#5E6D77]");
        tab.classList.remove("text-[#232323]");
      });
      activeTabButton.classList.remove("text-[#5E6D77]");
      activeTabButton.classList.add("text-[#232323]");

      lineRef.current.style.left = `${activeTabButton.offsetLeft}px`;
      lineRef.current.style.transition = "left 0.3s";
      lineRef.current.style.width = `${activeTabButton.clientWidth}px`;
    }
  }, [activeTab, headerHeight]);

  return (
    <div
      ref={tabsRef}
      style={{ top: headerHeight || 0 }}
      className={mergeClass(classTabs, `sticky z-20 w-full bg-white`)}
    >
      <div
        className={mergeClass(
          classInner,
          "flex w-full border-b border-[#EAEEF3] " +
            (tabs.length > 3 ? "justify-between" : "gap-8 sm:gap-10"),
        )}
      >
        {isDesktop ? (
          tabs.map((tab, index) => (
            <button
              id={
                "section-tab-" +
                (tab ?? "").toString().replace(/\s/g, "-").toLowerCase()
              }
              key={index}
              onClick={() => handleTabClick(index)}
              className="h-[3rem] text-center text-sm font-semibold text-[#5E6D77] sm:text-[1.125rem]"
            >
              {tab}
            </button>
          ))
        ) : (
          <Swiper slidesPerView={3} className="pass-swiper w-full">
            {tabs.map((tab, index) => (
              <SwiperSlide key={index}>
                <button
                  id={
                    "section-tab-" +
                    (tab ?? "").toString().replace(/\s/g, "-").toLowerCase()
                  }
                  onClick={() => handleTabClick(index)}
                  className="h-[3rem] w-full text-center text-sm font-semibold text-[#5E6D77] sm:text-[1.125rem]"
                >
                  {tab}
                </button>
              </SwiperSlide>
            ))}
          </Swiper>
        )}

        <div
          ref={lineRef}
          id="line"
          className="absolute bottom-[-0.125rem] min-w-[3.125rem] border-0 border-[#232323] sm:border-[0.125rem]"
        />
      </div>
    </div>
  );
};

export default Tabs;
